import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Progress, Table, Tag, Typography, DatePicker, Spin, Grid } from 'antd';
import ColumnsCardList from '../common/ColumnsCardList';
import { 
  ShoppingCartOutlined, 
  CheckCircleOutlined, 
  CloseCircleOutlined, 
  DeliveredProcedureOutlined, 
  DollarOutlined,
  FormOutlined,
  RiseOutlined
} from '@ant-design/icons';
import { ordersApi } from '../../utils/api';
import { PRIMARY_COLOR } from '../../theme/colors';
// Couleur de synthèse indépendante des séries du graphique.
import { PRIVATE_PRIMARY_COLOR } from '../../theme/privateTheme';
import { getDeviseActive } from '../../utils/devise';
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const OrdersDashboard = () => {
  // Mobile (< md) : cartes à la place du tableau ; `=== false` évite un flash avant la mesure
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const [loading, setLoading] = useState(false);
  const [statsData, setStatsData] = useState({
    totalOrders: 0,
    pendingOrders: 0,
    deliveredOrders: 0,
    cancelledOrders: 0,
    totalRevenue: 0,
    fulfillmentRate: 0
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [dateRange, setDateRange] = useState([null, null]);
  const [storefrontFunnel, setStorefrontFunnel] = useState({
    buttonClicks: 0,
    modalOpenings: 0,
    ordersPlaced: 0,
    orderConversionRate: 0
  });

  /**
   * Recharge les données du tableau de bord lorsque la période évolue.
   *
   * Les statistiques générales et le tunnel storefront utilisent exactement
   * les mêmes bornes de dates pour rendre leur comparaison compréhensible.
   */
  useEffect(() => {
    console.log('useEffect triggered. dateRange:', dateRange); // Log pour voir la valeur
    fetchDashboardData();
  }, [dateRange]);

  const fetchDashboardData = async () => {
    console.log('fetchDashboardData');

    setLoading(true);
    try {
      // Définir les paramètres de requête pour le filtrage par date
      let params = {};
      if (dateRange[0] && dateRange[1]) {
        params = {
          created_at_from: dateRange[0].toISOString(),
          created_at_to: dateRange[1].toISOString()
        };
      }

      // Récupération des statistiques globales
      const statsResponse = await ordersApi.getStats(params);
      
      if (statsResponse?.data) {
        const stats = statsResponse.data;
        setStatsData({
          totalOrders: stats.total_orders || 0,
          pendingOrders: stats.pending_orders || 0,
          deliveredOrders: stats.completed_orders || 0,
          cancelledOrders: stats.cancelled_orders || 0,
          totalRevenue: stats.total_revenue || 0,
          fulfillmentRate: stats.fulfillment_rate || 0
        });
      }

      // Le tunnel storefront est indépendant des commandes manuelles et POS.
      const storefrontFunnelResponse = await ordersApi.getStorefrontFunnelStats(params);
      if (storefrontFunnelResponse?.data) {
        const funnel = storefrontFunnelResponse.data;
        setStorefrontFunnel({
          buttonClicks: funnel.button_clicks || 0,
          modalOpenings: funnel.modal_openings || 0,
          ordersPlaced: funnel.orders_placed || 0,
          orderConversionRate: funnel.order_conversion_rate || 0
        });
      }

      // Récupération des 5 dernières commandes
      const recentOrdersResponse = await ordersApi.getRecent(params);
      
      if (recentOrdersResponse?.data) {
        const formattedOrders = recentOrdersResponse.data.map(order => ({
          id: order.id,
          order_number: `ORD-${order.order_number}`,
          customer_name: order.customer?.name || 'Client inconnu',
          status: order.status,
          total_amount: parseFloat(order.total_amount),
          created_at: order.created_at
        }));
        
        setRecentOrders(formattedOrders);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des données du tableau de bord:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusTag = (status) => {
    const tagSize = window.innerWidth < 768 ? 'small' : 'default';
    const tagClass = "text-xs";
    
    switch (status) {
      case 'new':
        return <Tag color="blue" size={tagSize} className={tagClass}>Nouvelle</Tag>;
      case 'confirmed':
        return <Tag color="cyan" size={tagSize} className={tagClass}>Confirmée</Tag>;
      case 'preparing':
        return <Tag color="purple" size={tagSize} className={tagClass}>En préparation</Tag>;
      case 'ready_to_deliver':
        return <Tag color="magenta" size={tagSize} className={tagClass}>Prête à livrer</Tag>;
      case 'in_delivery':
        return <Tag color="orange" size={tagSize} className={tagClass}>En livraison</Tag>;
      case 'completed':
        return <Tag color="green" size={tagSize} className={tagClass}>Livrée</Tag>;
      case 'cancelled':
        return <Tag color="red" size={tagSize} className={tagClass}>Annulée</Tag>;
      default:
        return <Tag size={tagSize} className={tagClass}>{status}</Tag>;
    }
  };

  const columns = [
    {
      title: 'Numéro',
      dataIndex: 'order_number',
      key: 'order_number',
      width: 100,
      render: (text) => (
        <span className="text-xs sm:text-sm font-medium">{text}</span>
      ),
    },
    {
      title: 'Client',
      dataIndex: 'customer_name',
      key: 'customer_name',
      width: 150,
      render: (text) => (
        <span className="text-xs sm:text-sm truncate block">{text}</span>
      ),
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <div className="flex justify-center">
          {getStatusTag(status)}
        </div>
      ),
    },
    {
      title: 'Montant',
      dataIndex: 'total_amount',
      key: 'total_amount',
      width: 90,
      align: 'right',
      render: (amount) => (
        <div className="text-right">
          <div className="text-xs sm:text-sm font-medium">{amount.toFixed(0)}</div>
          <div className="text-xs text-gray-500">{getDeviseActive()}</div>
        </div>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 90,
      // Masquer sur mobile très petit
      responsive: ['sm'],
      render: (date) => (
        <span className="text-xs sm:text-sm">
          {new Date(date).toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit'
          })}
        </span>
      ),
    },
  ];

  const handleDateRangeChange = (dates) => {
    console.log('handleDateRangeChange called with:', dates); // Log pour voir quand le picker change
    setDateRange(dates);
  };

  return (
    <Spin spinning={loading}>
      <div className="mb-4 flex justify-center sm:justify-end">
        <RangePicker 
          onChange={handleDateRangeChange} 
          size={window.innerWidth < 768 ? 'small' : 'middle'}
          placeholder={['Date début', 'Date fin']}
          className="w-full sm:w-auto max-w-xs"
        />
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total des commandes"
              value={statsData.totalOrders}
              prefix={<ShoppingCartOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Commandes en cours"
              value={statsData.pendingOrders}
              prefix={<CloseCircleOutlined />}
              valueStyle={{ color: PRIVATE_PRIMARY_COLOR }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Commandes livrées"
              value={statsData.deliveredOrders}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Chiffre d'affaires"
              value={statsData.totalRevenue}
              precision={2}
              prefix={<DollarOutlined />}
              suffix={getDeviseActive()}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
      </Row>

      <Card className="mt-4" title="Parcours de commande storefront">
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={6}>
            <Statistic
              title="Clics sur Commander maintenant"
              value={storefrontFunnel.buttonClicks}
              prefix={<ShoppingCartOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Statistic
              title="Modals de commande ouverts"
              value={storefrontFunnel.modalOpenings}
              prefix={<FormOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Statistic
              title="Commandes storefront passées"
              value={storefrontFunnel.ordersPlaced}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Statistic
              title="Conversion modal → commande"
              value={storefrontFunnel.orderConversionRate}
              precision={1}
              suffix="%"
              prefix={<RiseOutlined />}
              valueStyle={{ color: PRIVATE_PRIMARY_COLOR }}
            />
          </Col>
        </Row>
      </Card>

      <Row gutter={[16, 16]} className="mt-4">
        <Col xs={24} md={12}>
          <Card title="Taux de livraison">
            <div className="text-center">
              <Progress
                type="dashboard"
                percent={statsData.fulfillmentRate}
                strokeColor={{
                  '0%': '#108ee9',
                  '100%': '#87d068',
                }}
              />
              <div className="mt-4">
                <Text>Commandes livrées avec succès</Text>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card title="Répartition des commandes">
            <Row justify="space-around" align="middle" className="text-center">
              <Col span={8}>
                <Progress 
                  type="circle" 
                  percent={Math.round((statsData.deliveredOrders / statsData.totalOrders) * 100) || 0} 
                  strokeColor="#3f8600" 
                  width={window.innerWidth < 768 ? 60 : 80}
                  strokeWidth={window.innerWidth < 768 ? 8 : 6}
                />
                <div className="mt-1 sm:mt-2">
                  <Text className="text-xs sm:text-sm">Livrées</Text>
                </div>
              </Col>
              <Col span={8}>
                <Progress 
                  type="circle" 
                  percent={Math.round((statsData.pendingOrders / statsData.totalOrders) * 100) || 0} 
                  strokeColor={PRIMARY_COLOR}
                  width={window.innerWidth < 768 ? 60 : 80}
                  strokeWidth={window.innerWidth < 768 ? 8 : 6}
                />
                <div className="mt-1 sm:mt-2">
                  <Text className="text-xs sm:text-sm">En cours</Text>
                </div>
              </Col>
              <Col span={8}>
                <Progress 
                  type="circle" 
                  percent={Math.round((statsData.cancelledOrders / statsData.totalOrders) * 100) || 0} 
                  strokeColor="#cf1322" 
                  width={window.innerWidth < 768 ? 60 : 80}
                  strokeWidth={window.innerWidth < 768 ? 8 : 6}
                />
                <div className="mt-1 sm:mt-2">
                  <Text className="text-xs sm:text-sm">Annulées</Text>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Card title="Commandes récentes" className="mt-4">
        {isMobile ? (
          <ColumnsCardList columns={columns} dataSource={recentOrders} emptyText="Aucune commande récente" />
        ) : (
          <Table
            columns={columns}
            dataSource={recentOrders}
            rowKey="id"
            pagination={false}
            scroll={{ 
              x: 'max-content',
              scrollToFirstRowOnChange: true 
            }}
            size="middle"
          />
        )}
      </Card>
    </Spin>
  );
};

export default OrdersDashboard;
